import { forwardRef } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { Loading } from '@winnin/winnin_ui.components.loading'

import { RCTooltip } from 'components/RCTooltip'

import type {
  TBaseTableViewProps,
  TRowWrapperProps,
  TTableBodyItemProps,
  TTableHeaderItemProps,
  TTableHeaderProps,
  TTableHeadTitleProps,
  TTableRowProps,
} from './base-table.types'

const TableHeaders = ({ children }: TTableHeaderProps) => {
  return (
    <tr className="BaseTable-tableHead">
      <th />
      {children}
    </tr>
  )
}

const TableHeadTitle = ({ label, icon, tooltipMessage }: TTableHeadTitleProps) => {
  if (!label) {
    return null
  }

  if (icon) {
    return (
      <>
        {label}
        <RCTooltip tooltipMessage={tooltipMessage} hideTooltip={!tooltipMessage} direction="bottom">
          <Icon icon={icon} fill="var(--color_neutral_800)" />
        </RCTooltip>
      </>
    )
  }
  return (
    <RCTooltip tooltipMessage={tooltipMessage} hideTooltip={!tooltipMessage} direction="bottom">
      {label}
    </RCTooltip>
  )
}

const TableHeaderItem = ({
  item,
  isCurrentSelected,
  onTrack,
  customSort,
  handleSort,
  isItemAsc,
}: TTableHeaderItemProps) => {
  return (
    <th
      className={classNames([
        {
          'BaseTable-activeItem': isCurrentSelected,
          'BaseTable-title': item.isTitle,
          'BaseTable-hasSort': Boolean(item?.isSortable),
        },
      ])}
    >
      <TableHeadTitle tooltipMessage={item?.tooltipMessage} label={item.label} icon={item.icon} />

      {item?.isSortable && (
        <button
          className={classNames([{ 'BaseTable-activeItem': isCurrentSelected }, 'BaseTable-sortButton'])}
          onClick={() => {
            onTrack?.(item.key, !isItemAsc ? 'asc' : 'desc')
            if (customSort) {
              customSort(item.key)
              return
            }
            handleSort(item.key)
          }}
        >
          {isItemAsc && isCurrentSelected ? (
            <Icon icon="arrowUp" fill={isCurrentSelected ? 'var(--color_primary_500)' : 'var(--color_neutral_800)'} />
          ) : (
            <Icon icon="arrowDown" fill={isCurrentSelected ? 'var(--color_primary_500)' : 'var(--color_neutral_800)'} />
          )}
        </button>
      )}
    </th>
  )
}

const TableBody = ({ children, isLoading, loadingComponent }: TTableBodyItemProps) => {
  if (isLoading) {
    return <>{loadingComponent ? loadingComponent : <Loading />}</>
  }
  return <tbody>{children}</tbody>
}

const RowWrapper = ({ href, target, children }: TRowWrapperProps) => {
  if (!href) {
    return <>{children}</>
  }

  return (
    <Link to={href} target={target}>
      {children}
    </Link>
  )
}

const TableRow = forwardRef(({ color, headers, index, item, colors, onClick }: TTableRowProps, ref: any) => {
  const handleClick = () => {
    if (onClick) {
      onClick(item)
    }
  }

  return (
    <div className="BaseTable-TableRow" ref={ref}>
      <RowWrapper href={item?.href} target={item?.target}>
        <tr
          className={classNames(['BaseTable-tableBody', { 'BaseTable-hasClick': Boolean(onClick) }])}
          onClick={handleClick}
        >
          <td className="BaseTable-numberSection">
            {colors ? <div className="BaseTable-colorDot" style={{ backgroundColor: color }} /> : index + 1}
          </td>
          {headers.map(header => {
            if (header.component) {
              const Component = header.component
              return (
                <td key={`item-${item?.id}-${header.key}`}>
                  <Component value={item?.[header.key]} row={item} index={index} />
                </td>
              )
            }

            return (
              <td key={`item-${item?.id}-${header.key}`} className={classNames({ 'BaseTable-title': header?.isTitle })}>
                {item?.[header.key] ?? '--'}
              </td>
            )
          })}
        </tr>
      </RowWrapper>
    </div>
  )
})

const Component = ({ children, className }: TBaseTableViewProps) => {
  return <table className={classNames(['BaseTable-container', { [className ?? '']: className }])}>{children}</table>
}

Component.TableHeadTitle = TableHeadTitle
Component.TableHeaders = TableHeaders
Component.TableHeaderItem = TableHeaderItem
Component.TableBody = TableBody
Component.TableRow = TableRow

export const BaseTableView = Component
